import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getPodList} from "gui-common/redux/action/efe/podAction";
import {RootState} from "gui-common/redux/store";
import {Pod} from "gui-common/redux/data/pod";
import {Button, Grid, Header, Input, Modal, ProgressBar, styles, Surface, Text, useTheme} from "tmwx-design-web/src";
import {useTranslation} from "react-i18next";
import {isEmpty} from "gui-common/appHelper";
import {usePermissions} from "tmwx-design-common";
import {Company} from "gui-common/redux/data/company";
import {Box, Chip, Divider, Stack} from "@mui/material";
import {podActions} from "gui-common/redux/slice/efe/podSlice";

function ModalRow({pod, handleOnClick}: { pod: Pod, handleOnClick: (pod: Pod) => void }) {
    const {padding, colors, roundness} = useTheme()
    const {t} = useTranslation();

    return (
        <Box
            onClick={() => handleOnClick(pod)}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '160px minmax(0, 1fr) 160px auto' },
                gap: 1,
                alignItems: 'center',
                px: padding,
                py: 1,
                borderRadius: `${roundness}px`,
                backgroundColor: colors.elevation.level0,
                border: `1px solid ${colors.elevation.level2}`,
                transition: 'transform 140ms ease, background-color 140ms ease, border-color 140ms ease',
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: colors.elevation.level1,
                    borderColor: colors.elevation.level3,
                    transform: 'translateY(-1px)',
                }
            }}
        >
            <Box>
                <Text bold>{pod.podId}</Text>
            </Box>

            <Box sx={{ minWidth: 0 }}>
                <Text bold style={{ marginBottom: 0 }}>
                    {pod.customerName}
                </Text>
                <Text secondary variant="helper">
                    {pod.systemId}
                </Text>
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Chip
                    size="small"
                    label={pod.systemId}
                    variant="outlined"
                    sx={{
                        borderColor: colors.elevation.level3,
                        color: colors.onSurface,
                    }}
                />
            </Box>

            <Box sx={{ justifySelf: { xs: 'start', md: 'end' } }}>
                <Button
                    size="small"
                    variant="contained"
                    onPress={() => handleOnClick(pod)}
                >
                    {t('select')}
                </Button>
            </Box>
        </Box>
    );
}

const EfeSearchModal = ({label}: { label: string }) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const { hasRights } = usePermissions()

    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('')

    const loading = useSelector<RootState, boolean>(state => state.pod.loading);
    const podList = useSelector<RootState, Pod[]>(state => state.pod.podList);
    const activeCompany = useSelector<RootState, Company>(state => state.auth.activeCompany);
    const {colors, padding, roundness} = useTheme();

    useEffect(() => {
        if (hasRights(["efe_pod_list"])) {
            dispatch(getPodList(activeCompany.id));
        }
    }, [dispatch, activeCompany, hasRights]);

    const handleOnClick = (selectedPod: Pod) => {
        dispatch(podActions.setSelectedPod(selectedPod))
        handleClose()
    }

    const handleClose = () => {
        setIsDialogOpen(false);
    };

    const handleDialogOpen = () => {
        setIsDialogOpen(true);
    };

    const filteredPods = useMemo(() => {
        const query = search.trim().toLowerCase();

        if (isEmpty(query)) {
            return podList;
        }

        return podList.filter((val) => (
            val.podId?.toLowerCase().includes(query) ||
            val.systemId?.toLowerCase().includes(query) ||
            val.customerName?.toLowerCase().includes(query)
        ));
    }, [podList, search]);

    return (
        <Surface style={{ marginBottom: 4, marginLeft: 4, marginRight: 4, width: '100%', flex: 1, minWidth: 560 }}>
            <Box
                onClick={handleDialogOpen}
                sx={{
                    cursor: 'pointer',
                    position: 'relative',
                    width: '100%',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: `${roundness}px`,
                        pointerEvents: 'none',
                    }
                }}
            >
                <Input
                    style={{ ml: 1, fontWeight: 500, pointerEvents: 'none', width: '100%' }}
                    fullWidth
                    icon={"search"}
                    placeholder={t('efe.efeSearchId')}
                    value={label}
                    onChangeValue={() => {}}
                />
            </Box>

            <Modal
                visible={isDialogOpen}
                type='dialog'
                dialogMaxWidth='m'
                contentElevation={0}
                closeModal={handleClose}
                header={(
                    <Header
                        title={t('efe.efeSearch')}
                        subtitle={t('efe.efeSearchId')}
                        actionRight={<Button iconLeft={'clear'} status='neutral' variant='ghost'
                                             onPress={handleClose}/>}
                    />
                )}
            >
                <Surface elevation={0} style={{ padding }}>
                    <Stack spacing={2}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'stretch', md: 'center' },
                                justifyContent: 'space-between',
                                gap: 1.5,
                                px: 0.5,
                            }}
                        >
                            <Box>
                                <Text bold style={{ fontSize: 18 }}>
                                    {t('efe.efeSearch')}
                                </Text>
                                <Text secondary variant="helper">
                                    {t('efe.efeSearchId')}
                                </Text>
                            </Box>

                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip size="small" label={`${podList.length} ${t('rowsSelected')}`} />
                                <Chip size="small" label={activeCompany?.name ?? ''} variant="outlined" />
                            </Stack>
                        </Box>

                        <Box sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: colors.elevation.level0, pt: 0.25, pb: 0.75 }}>
                            <Input
                                style={{ ml: 1, fontWeight: 500 }}
                                fullWidth
                                icon={"search"}
                                placeholder={t('efe.efeSearchId')}
                                value={search}
                                onChangeValue={(text) => setSearch(text)}
                            />
                        </Box>

                        <Divider />

                        <Box sx={{ maxHeight: '52vh', overflowY: 'auto', pr: 0.5 }}>
                            {(loading) ? (
                                <ProgressBar animated rounded={false} width={'100%'} height={6} vertical={false}/>
                            ) : isEmpty(filteredPods) ? (
                                <Box
                                    sx={{
                                        p: 3,
                                        textAlign: 'center',
                                        borderRadius: `${roundness}px`,
                                        backgroundColor: colors.elevation.level0,
                                        border: `1px dashed ${colors.elevation.level2}`,
                                    }}
                                >
                                    <Text bold style={{ fontSize: 16 }}>
                                        {isEmpty(search) ? t('noData') : t('noResult')}
                                    </Text>
                                    <Text secondary variant="helper" style={{ marginTop: 6 }}>
                                        {isEmpty(search)
                                            ? t('efe.efeSearchId')
                                            : `${search} ${t('filter')}`
                                        }
                                    </Text>
                                </Box>
                            ) : (
                                <Stack spacing={1}>
                                    {filteredPods.map((item) => (
                                        <ModalRow
                                            key={item.id}
                                            pod={item}
                                            handleOnClick={handleOnClick}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                </Surface>
            </Modal>
        </Surface>
    )
}

export default EfeSearchModal;
